import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import './AddHolidayDialog.css';

const AddHolidayDialog = ({ isOpen, holiday, onClose, onSaved }) => {
    const isEdit = !!holiday;
    const [form, setForm] = useState({
        occasion: '',
        occasionBn: '',
        startDate: '',
        endDate: '',
        isMoonDependent: false,
        isRange: false
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (isOpen) {
            if (holiday) {
                const fmt = (d) => new Date(d).toISOString().slice(0, 10);
                const start = fmt(holiday.startDate);
                const end = fmt(holiday.endDate);
                setForm({
                    occasion: holiday.occasion || '',
                    occasionBn: holiday.occasionBn || '',
                    startDate: start,
                    endDate: end,
                    isMoonDependent: holiday.isMoonDependent || false,
                    isRange: start !== end
                });
            } else {
                setForm({ occasion: '', occasionBn: '', startDate: '', endDate: '', isMoonDependent: false, isRange: false });
            }
            setError('');
        }
    }, [isOpen, holiday]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm(prev => {
            const updated = { ...prev, [name]: type === 'checkbox' ? checked : value };
            // When toggling off range, sync endDate to startDate
            if (name === 'isRange' && !checked) {
                updated.endDate = updated.startDate;
            }
            // When single day, keep endDate = startDate automatically
            if (name === 'startDate' && !prev.isRange) {
                updated.endDate = value;
            }
            return updated;
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const payload = {
                occasion: form.occasion.trim(),
                occasionBn: form.occasionBn.trim(),
                startDate: form.startDate,
                endDate: form.isRange ? form.endDate : form.startDate,
                isMoonDependent: form.isMoonDependent
            };

            let response;
            if (isEdit) {
                response = await api.put(`/holidays/${holiday._id}`, payload);
            } else {
                response = await api.post('/holidays', payload);
            }

            if (response.data.success) {
                onSaved(response.data.data, isEdit);
                onClose();
            } else {
                setError(response.data.message || 'Failed to save holiday');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'An error occurred');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="holiday-dialog-overlay" onClick={onClose}>
            <div className="holiday-dialog" onClick={e => e.stopPropagation()}>
                <div className="holiday-dialog-header">
                    <h3>{isEdit ? 'Edit Holiday' : 'Add Holiday'}</h3>
                    <button className="holiday-dialog-close" onClick={onClose}>✕</button>
                </div>

                <form className="holiday-dialog-form" onSubmit={handleSubmit}>
                    {error && <div className="holiday-form-error">{error}</div>}

                    <div className="holiday-form-group">
                        <label>Occasion (English) *</label>
                        <input
                            type="text"
                            name="occasion"
                            value={form.occasion}
                            onChange={handleChange}
                            placeholder="e.g. Eid-Ul-Fitr"
                            required
                        />
                    </div>

                    <div className="holiday-form-group">
                        <label>Occasion (Bengali)</label>
                        <input
                            type="text"
                            name="occasionBn"
                            value={form.occasionBn}
                            onChange={handleChange}
                            placeholder="e.g. ঈদ-উল-ফিতর"
                        />
                    </div>

                    <div className="holiday-form-row">
                        <div className="holiday-form-group">
                            <label>{form.isRange ? 'Start Date *' : 'Date *'}</label>
                            <input
                                type="date"
                                name="startDate"
                                value={form.startDate}
                                onChange={handleChange}
                                required
                            />
                        </div>
                        {form.isRange && (
                            <div className="holiday-form-group">
                                <label>End Date *</label>
                                <input
                                    type="date"
                                    name="endDate"
                                    value={form.endDate}
                                    onChange={handleChange}
                                    min={form.startDate}
                                    required
                                />
                            </div>
                        )}
                    </div>

                    <div className="holiday-form-checks">
                        <label className="holiday-checkbox-label">
                            <input
                                type="checkbox"
                                name="isRange"
                                checked={form.isRange}
                                onChange={handleChange}
                            />
                            <span>Date range (multiple days)</span>
                        </label>
                        <label className="holiday-checkbox-label">
                            <input
                                type="checkbox"
                                name="isMoonDependent"
                                checked={form.isMoonDependent}
                                onChange={handleChange}
                            />
                            <span>Moon-sighting dependent *</span>
                        </label>
                    </div>

                    <div className="holiday-dialog-footer">
                        <button type="button" className="holiday-cancel-btn" onClick={onClose}>
                            Cancel
                        </button>
                        <button type="submit" className="holiday-save-btn" disabled={loading}>
                            {loading ? 'Saving...' : isEdit ? 'Save Changes' : 'Add Holiday'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddHolidayDialog;
